import Button from '@mortgage-pos/ui/components/Button'
import { iconMap } from '@mortgage-pos/ui/utils/customizationHelpers'
import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { supportedFileFormats } from '@mortgage-pos/ui/data/supportedFileFormats'
import useMediaQuery from '@mortgage-pos/ui/hooks/useMediaQuery'

interface FileUploadProps {
  fileUploadHandler: (file: File) => void
  uploadButtonText?: string
}

const FileUpload: FC<FileUploadProps> = ({
  fileUploadHandler,
  uploadButtonText,
}) => {
  const [isDragOverSuccess, setIsDragOverSuccess] = useState(false)
  const [dragLevel, setDragLevel] = useState(0)

  const fileInputField = useRef(null)
  const dragAndDropDivField = useRef(null)

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.currentTarget.files)
    fileUploadHandler(newFiles[0])
  }

  const onUploadClick = () => fileInputField.current.click()

  const onDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()

    const { items } = e.dataTransfer
    if (!items) return

    const files = Array.from(items)
    const hasInvalidFile = files.reduce(
      (result, file) =>
        result || file.kind !== 'file' || !file.type.includes('pdf'),
      false
    )

    setDragLevel((p) => p + 1)
    setIsDragOverSuccess(!hasInvalidFile)
  }

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setDragLevel((p) => p - 1)
  }

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const onDragDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()

    const { files: rawFiles } = e.dataTransfer
    const files = rawFiles ? Array.from(rawFiles) : []

    const validFileType = supportedFileFormats.some((file) => {
      return files[0].type.includes(file)
    })

    const hasInvalidFile = files.length !== 1 || !validFileType

    if (hasInvalidFile) {
      setDragLevel(0)
      return
    }

    fileUploadHandler(files[0])
  }

  const UploadButtonText = useMediaQuery(
    '(max-width: 950px)',
    'Take a picture',
    uploadButtonText
  )

  useEffect(() => {
    const div = dragAndDropDivField.current
    if (!div) return
    div.addEventListener('dragenter', onDragEnter)
    div.addEventListener('dragleave', onDragLeave)
    div.addEventListener('dragover', onDragOver)
    div.addEventListener('drop', onDragDrop)
    return () => {
      div.removeEventListener('dragenter', onDragEnter)
      div.removeEventListener('dragleave', onDragLeave)
      div.removeEventListener('dragover', onDragOver)
      div.removeEventListener('drop', onDragDrop)
    }
  }, [dragAndDropDivField])

  const fileUploadClasses = useMemo(() => {
    const baseClass = 'file-upload'
    if (!dragLevel) return baseClass

    if (isDragOverSuccess) {
      return `${baseClass} success`
    }

    return `${baseClass} failure`
  }, [dragLevel, isDragOverSuccess])

  return (
    <div
      className={fileUploadClasses}
      onClick={onUploadClick}
      ref={dragAndDropDivField}
    >
      <img
        src={iconMap('save')}
        className="file-upload__image"
        alt="upload icon"
      />
      <p className="file-upload__body">
        Drag and drop file here or upload manually
      </p>
      <Button
        className="file-upload__button"
        content={UploadButtonText || 'Upload document'}
      />
      <input
        type="file"
        ref={fileInputField}
        onChange={handleFileUpload}
        pattern="^.+\.pdf$"
        title=""
        value=""
      />
    </div>
  )
}

export default FileUpload
